// ==========================================
// MEMBERS API - Following API Implementation Guide
// ==========================================
// This file provides React Query hooks and API functions for organization member management
// Pattern reference: /docs/api-implementation-guide.md

// 1. IMPORTS
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { API, apiClient } from '../api_clients';
import { useOrganizationStore } from '../stores/organization-store';
import { extractErrorMessage } from './axiosIntance';
import { toast } from 'sonner';

// 2. TYPE EXPORTS - Re-export API types
export type OrganizationMember = API.ResponsesOrganizationMember;
export type OrganizationMembersResponse = API.ResponsesOrganizationMembersResponse;
export type OrganizationInvitation = API.ResponsesOrganizationInvitation;
export type PendingInvitation = API.ResponsesPendingInvite;
export type WithCognitoAuth = API.RequestsWithCognitoAuth;

// Request types
export interface InviteMemberRequest {
  email: string;
  roles: string[];
  message?: string; // Optional invitation message (maxLength: 500)
  WithCognitoAuth?: WithCognitoAuth;
}

export interface UpdateMemberRolesRequest {
  roles: string[];
  WithCognitoAuth?: WithCognitoAuth;
}

export interface ResendInvitationRequest {
  WithCognitoAuth?: WithCognitoAuth;
}

export interface AcceptInvitationRequest {
  WithCognitoAuth?: WithCognitoAuth;
}

// Query parameters for listing members
export interface MembersQueryParams {
  page?: number;
  pageSize?: number;
}

// Response data type
export interface MembersListData {
  members: OrganizationMember[];
  pendingInvitations: PendingInvitation[];
  page?: number;
  pageSize?: number;
  count?: number;
}

// 3. API FUNCTIONS

/**
 * Fetches paginated list of organization members
 * Returns raw API response - mapping happens in the hook
 */
async function getMembers(
  organizationUnid: string,
  params: MembersQueryParams = {}
): Promise<MembersListData> {
  const response = await apiClient.organizationsApi.getOrganizationMembers(
    organizationUnid,
    '', // authorization - handled by interceptor
    params.page,
    params.pageSize,
    undefined // origin
  );

  const data = response.data.data;
  if (!data) {
    throw new Error('Failed to fetch members');
  }

  return {
    members: data.members || [],
    pendingInvitations: data.pendingInvitations || [],
    page: data.page,
    pageSize: data.pageSize,
    count: data.count,
  };
}

/**
 * Invites a new member to the organization
 */
async function inviteMember(
  organizationUnid: string,
  data: InviteMemberRequest
): Promise<OrganizationInvitation> {
  // Validation
  if (!data.email || data.email.trim() === '') {
    throw new Error('Email is required');
  }

  if (!data.roles || data.roles.length === 0) {
    throw new Error('At least one role is required');
  }

  if (data.message && data.message.length > 500) {
    throw new Error('Message must be 500 characters or less');
  }

  const response = await apiClient.organizationsApi.createInvitation(
    organizationUnid,
    '', // authorization - handled by interceptor
    {
      email: data.email,
      roles: data.roles,
      message: data.message,
      WithCognitoAuth: data.WithCognitoAuth,
    },
    undefined // origin
  );

  const invitation = response.data.data?.invitation;
  if (!invitation) {
    throw new Error('Failed to create invitation');
  }

  return invitation;
}

/**
 * Updates member roles
 */
async function updateMemberRoles(
  organizationUnid: string,
  userUnid: string,
  data: UpdateMemberRolesRequest
): Promise<void> {
  // Validation
  if (!data.roles || data.roles.length === 0) {
    throw new Error('At least one role is required');
  }

  await apiClient.organizationsApi.updateMemberRoles(
    organizationUnid,
    userUnid,
    '', // authorization - handled by interceptor
    {
      roles: data.roles,
      WithCognitoAuth: data.WithCognitoAuth,
    },
    undefined // origin
  );
}

/**
 * Removes a member from the organization
 */
async function removeMember(
  organizationUnid: string,
  userUnid: string
): Promise<void> {
  await apiClient.organizationsApi.removeMember(
    organizationUnid,
    userUnid,
    '', // authorization - handled by interceptor
    undefined // origin
  );
}

/**
 * Deletes a pending invitation
 */
async function deleteInvitation(
  organizationUnid: string,
  invitationUnid: string
): Promise<void> {
  await apiClient.organizationsApi.deleteInvitation(
    invitationUnid,
    organizationUnid,
    '', // authorization - handled by interceptor
    undefined // origin
  );
}

/**
 * Resends a pending invitation
 */
async function resendInvitation(
  organizationUnid: string,
  invitationUnid: string,
  data?: ResendInvitationRequest
): Promise<void> {
  await apiClient.organizationsApi.resendInvitation(
    invitationUnid,
    organizationUnid,
    '', // authorization - handled by interceptor
    {
      WithCognitoAuth: data?.WithCognitoAuth,
    },
    undefined // origin
  );
}

/**
 * Accepts an invitation
 */
async function acceptInvitation(
  token: string,
  data?: AcceptInvitationRequest
): Promise<void> {
  await apiClient.organizationsApi.acceptInvitation(
    token,
    '', // authorization - handled by interceptor
    {
      WithCognitoAuth: data?.WithCognitoAuth,
    },
    undefined // origin
  );
}

/**
 * Fetches the logged-in user's pending invitations (invitations sent TO them)
 */
export async function getMyPendingInvitations(): Promise<API.ResponsesUserInvitation[]> {
  const response = await apiClient.organizationsApi.getMyInvitations(
    '', // authorization - handled by interceptor
    undefined // origin
  );
  return response.data.data?.invitations || [];
}

/**
 * Accepts an invitation by token (for the logged-in user's own invitations)
 */
export async function acceptMyInvitation(token: string): Promise<void> {
  await apiClient.organizationsApi.acceptInvitation(
    token,
    '', // authorization - handled by interceptor
    {},
    undefined // origin
  );
}
// 4. REACT QUERY HOOKS

/**
 * Hook to fetch paginated list of organization members
 * @param params - Query parameters for pagination
 * @returns React Query hook with members data, pending invitations, and pagination info
 */
export function useMembers(params: MembersQueryParams = {}) {
  const { organization } = useOrganizationStore();

  const query = useQuery({
    queryKey: ['members', organization?.id, params],
    queryFn: () => getMembers(organization?.id || '', params),
    enabled: !!organization?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Handle errors with toast
  useEffect(() => {
    if (query.error) {
      const errorMessage = extractErrorMessage(query.error);
      toast.error('Failed to fetch members', {
        description: errorMessage,
      });
    }
  }, [query.error]);

  return query;
}

/**
 * Hook to invite a new member to the organization
 * @returns React Query mutation hook
 */
export function useInviteMember() {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteMemberRequest) =>
      inviteMember(organization?.id || '', data),
    onSuccess: () => {
      // Invalidate members list to refetch (includes pending invitations)
      queryClient.invalidateQueries({
        queryKey: ['members', organization?.id],
      });

      toast.success('Invitation sent successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to send invitation', {
        description: errorMessage,
      });
    },
  });
}


export function useAcceptMyInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => acceptMyInvitation(token),
    onSuccess: () => {
      // Invalidate members list to refetch
      queryClient.invalidateQueries({
        queryKey: ['members'],
      });

      toast.success('Invitation accepted successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to accept invitation', {
        description: errorMessage,
      });
    },
  });
}
/**
 * Hook to update member roles
 * @returns React Query mutation hook
 */
export function useUpdateMemberRoles() {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userUnid,
      data,
    }: {
      userUnid: string;
      data: UpdateMemberRolesRequest;
    }) => updateMemberRoles(organization?.id || '', userUnid, data),
    onSuccess: () => {
      // Invalidate members list to refetch
      queryClient.invalidateQueries({
        queryKey: ['members', organization?.id],
      });

      toast.success('Member roles updated successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to update member roles', {
        description: errorMessage,
      });
    },
  });
}

export function useMyPendingInvitations() {
  return useQuery({
    queryKey: ['my-pending-invitations'],
    queryFn: () => getMyPendingInvitations(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to remove a member from the organization
 * @returns React Query mutation hook
 */
export function useRemoveMember() {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userUnid: string) =>
      removeMember(organization?.id || '', userUnid),
    onSuccess: () => {
      // Invalidate members list to refetch
      queryClient.invalidateQueries({
        queryKey: ['members', organization?.id],
      });

      toast.success('Member removed successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to remove member', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to delete a pending invitation
 * @returns React Query mutation hook
 */
export function useDeleteInvitation() {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationUnid: string) =>
      deleteInvitation(organization?.id || '', invitationUnid),
    onSuccess: () => {
      // Invalidate members list to refetch (updates pending invitations)
      queryClient.invalidateQueries({
        queryKey: ['members', organization?.id],
      });

      toast.success('Invitation deleted successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to delete invitation', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to resend a pending invitation
 * @returns React Query mutation hook
 */
export function useResendInvitation() {
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      invitationUnid,
      data,
    }: {
      invitationUnid: string;
      data?: ResendInvitationRequest;
    }) => resendInvitation(organization?.id || '', invitationUnid, data),
    onSuccess: () => {
      // Invalidate members list to refetch (updates pending invitations)
      queryClient.invalidateQueries({
        queryKey: ['members', organization?.id],
      });

      toast.success('Invitation resent successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to resend invitation', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to accept an invitation
 * @returns React Query mutation hook
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      token,
      data,
    }: {
      token: string;
      data?: AcceptInvitationRequest;
    }) => acceptInvitation(token, data),
    onSuccess: () => {
      // Invalidate members list to refetch (user becomes a member)
      queryClient.invalidateQueries({
        queryKey: ['members'],
      });

      toast.success('Invitation accepted successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to accept invitation', {
        description: errorMessage,
      });
    },
  });
}
